import React, { useEffect, useRef, useState } from "react";
import { Alert, Card, Col, Grid, Layout, Row, Skeleton, Space } from "antd";

import { PermissionKey } from "@fitness-app/data-models";
import { surveysTemplatesActions } from "@fitness-app/app-store";

import { usePermission } from "~/hooks/usePermission";
import { ChatUtilitiesProvider } from "~/modules/Chat/providers/ChatUtilitiesProvider";
import { TraineeFloatingButton } from "~/modules/Trainee/components/TraineeFloatingButton/TraineeFloatingButton";
import ProfileInfo from "~/modules/Trainee/TraineeProfile/components/ProfileInfo";
import { TraineeActivityAndTasks } from "~/modules/Trainee/TraineeProfile/components/TraineeActivityAndTasks";
import { TraineeDashboardNotes } from "~/modules/Trainee/TraineeProfile/components/TraineeDashboardNotes";
import TraineeFeatures from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeFeatures";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import TraineeStats from "./components/TraineeStats";
import { TraineeScopeProvider, useTraineeId, useTraineeProfile, useTraineeRealtime } from "./scope";

type TraineeProfileProps = Omit<React.ComponentProps<typeof TraineeScopeProvider>, "children">;

const TraineeProfile = (props: TraineeProfileProps) => (
  <TraineeScopeProvider {...props}>
    <TraineeProfileContent />
  </TraineeScopeProvider>
);

const TraineeProfileContent = () => {
  const id = useTraineeId();
  const dispatch = useAppDispatch();
  const { data: profile, isError, isLoading } = useTraineeProfile();
  useTraineeRealtime(id);
  const surveyTemplates = useAppSelector((store) => store.surveysTemplates.list);
  const screens = Grid.useBreakpoint();
  const profileColRef = useRef<HTMLDivElement>(null);
  const [profileHeight, setProfileHeight] = useState<number>();
  // TRE-1636: sales reps see a trimmed profile — basic info, tags and notes only.
  // Pływający przycisk to wysłanie wiadomości i dodanie notatki.
  const canWriteToClients = usePermission(PermissionKey.CHAT_WRITE);

  useEffect(() => {
    const el = profileColRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height) {
        setProfileHeight(Math.round(height));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (surveyTemplates.length === 0) {
      void dispatch(surveysTemplatesActions.fetchSurveysTemplates());
    }
  }, []);

  if (isError) {
    return <Alert type="error" message="Nie udało się pobrać profilu" />;
  }

  return (
    <Layout className="p-3 lg:p-6">
      <Space direction="vertical" size={16} className="w-full">
        <TraineeDashboardNotes traineeId={id} />
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <div ref={profileColRef}>
              <ProfileInfo />
            </div>
          </Col>
          <Col xs={24} xl={12}>
            <TraineeActivityAndTasks maxHeight={screens.xl ? profileHeight : undefined} />
          </Col>
        </Row>
        <TraineeStats />
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            {isLoading || !profile ? (
              <Card>
                <Skeleton active loading round paragraph={{ rows: 5 }} />
              </Card>
            ) : (
              <TraineeFeatures />
            )}
          </Col>
        </Row>
      </Space>
      {/* Montowany w każdej karcie: ukryta karta ma visibility:hidden, które chowa też przycisk
          `fixed`, a przełączanie kart nie montuje i nie odmontowuje jego portali. */}
      {canWriteToClients && (
        <ChatUtilitiesProvider>
          <TraineeFloatingButton />
        </ChatUtilitiesProvider>
      )}
    </Layout>
  );
};

export default TraineeProfile;
